import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../config/axios';
import { Invoice, InvoiceFormData } from '../../types/invoice';
import aiInvoiceService from '../../services/aiInvoiceService';
import { InvoiceAIData, ProcessingStep } from '../../types/aiInvoice';

interface InvoiceState {
  invoices: Invoice[];
  currentInvoice: Invoice | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  aiProcessing: {
    isProcessing: boolean;
    isCreating: boolean;
    currentStep: 'idle' | 'upload' | 'ai_extraction' | 'ai_validation' | 'complete' | 'error';
    progress: number;
    extractedData: InvoiceAIData | null;
    statusHistory: ProcessingStep[];
    error: string | null;
  };
}

const initialState: InvoiceState = {
  invoices: [],
  currentInvoice: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 25, // Reduced for better Safari performance
    total: 0,
    pages: 0,
  },
  aiProcessing: {
    isProcessing: false,
    isCreating: false,
    currentStep: 'idle',
    progress: 0,
    extractedData: null,
    statusHistory: [],
    error: null,
  },
};

// Async thunks
export const fetchInvoices = createAsyncThunk(
  'invoices/fetchInvoices',
  async (params: { page?: number; limit?: number; status?: string; search?: string; businessUnitId?: string } = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.status) queryParams.append('status', params.status);
      if (params?.search) queryParams.append('search', params.search);
      if (params?.businessUnitId) queryParams.append('businessUnitId', params.businessUnitId);
      
      const response = await api.get(`/invoices?${queryParams.toString()}`);
      return {
        invoices: response.data.invoices,
        pagination: response.data.pagination,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch invoices');
    }
  }
);

export const fetchInvoiceById = createAsyncThunk(
  'invoices/fetchInvoiceById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await api.get(`/invoices/${id}`);
      return response.data.invoice;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch invoice');
    }
  }
);



export const updateInvoice = createAsyncThunk(
  'invoices/updateInvoice',
  async ({ id, data }: { id: number; data: Partial<Invoice> }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/invoices/${id}`, data);
      return response.data.invoice;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update invoice');
    }
  }
);

export const updateInvoiceProcessingStatus = createAsyncThunk(
  'invoices/updateInvoiceProcessingStatus',
  async ({ id, processingStatus }: { id: number; processingStatus: string }, { rejectWithValue }) => {
    try {
      // Update processingStatus field, not the main status field
      const response = await api.patch(`/invoices/${id}/status`, { 
        status: 'pending', // Keep main status as pending
        processingStatus: processingStatus // Update the processing workflow status
      });
      return response.data.invoice;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update invoice processing status');
    }
  }
);

export const deleteInvoice = createAsyncThunk(
  'invoices/deleteInvoice',
  async (id: number, { rejectWithValue }) => {
    try {
      await api.delete(`/invoices/${id}`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete invoice');
    }
  }
);

export const getReadyInvoices = createAsyncThunk(
  'invoices/getReadyInvoices',
  async (businessUnitId: string | undefined, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('status', 'ready_for_payment');
      if (businessUnitId && businessUnitId !== 'all') {
        queryParams.append('businessUnitId', businessUnitId);
      }
      
      const response = await api.get(`/invoices?${queryParams.toString()}`);
      return response.data.invoices;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch ready invoices');
    }
  }
);

export const bulkDeleteInvoices = createAsyncThunk(
  'invoices/bulkDeleteInvoices',
  async (ids: number[], { rejectWithValue }) => {
    try {
      const response = await api.delete('/invoices/bulk', { data: { ids } });
      return { deletedCount: response.data.deletedCount, ids };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete invoices');
    }
  }
);

export const bulkUpdateInvoiceStatus = createAsyncThunk(
  'invoices/bulkUpdateInvoiceStatus',
  async ({ ids, status }: { ids: number[]; status: string }, { rejectWithValue }) => {
    try {
      const response = await api.patch('/invoices/bulk/status', { ids, status });
      return { updatedCount: response.data.updatedCount, ids, status };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update invoice statuses');
    }
  }
);

// AI Invoice Processing Thunks
export const processInvoiceWithAI = createAsyncThunk(
  'invoices/processInvoiceWithAI',
  async (file: File, { rejectWithValue }) => {
    try {
      const result = await aiInvoiceService.processInvoiceWithAI(file);
      return result;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to process invoice with AI');
    }
  }
);

export const createInvoiceFromAI = createAsyncThunk(
  'invoices/createInvoiceFromAI',
  async ({ invoiceData, pdfFile }: { invoiceData: InvoiceAIData; pdfFile: File }, { rejectWithValue }) => {
    try {
      const result = await aiInvoiceService.createInvoiceFromAI(invoiceData, pdfFile);
      return result;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create invoice');
    }
  }
);

const invoiceSlice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentInvoice: (state) => {
      state.currentInvoice = null;
    },
    setPagination: (state, action: PayloadAction<{ page: number; limit: number }>) => {
      state.pagination.page = action.payload.page;
      state.pagination.limit = action.payload.limit;
    },
    clearAIProcessing: (state) => {
      state.aiProcessing = {
        isProcessing: false,
        isCreating: false,
        currentStep: 'idle',
        progress: 0,
        extractedData: null,
        statusHistory: [],
        error: null,
      };
    },
    clearAIError: (state) => {
      state.aiProcessing.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Invoices
      .addCase(fetchInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = action.payload.invoices;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Invoice by ID
      .addCase(fetchInvoiceById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoiceById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentInvoice = action.payload;
      })
      .addCase(fetchInvoiceById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Update Invoice
      .addCase(updateInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateInvoice.fulfilled, (state, action) => {
        state.loading = false;
        const updatedInvoice = action.payload;
        
        // Update the invoice in the invoices list by merging changes
        const index = state.invoices.findIndex(invoice => invoice.id === updatedInvoice.id);
        if (index !== -1) {
          // Ensure we preserve all existing fields and update with new data
          state.invoices[index] = { ...state.invoices[index], ...updatedInvoice };
        }
        
        // Update currentInvoice if it's the same invoice
        if (state.currentInvoice?.id === updatedInvoice.id) {
          state.currentInvoice = { ...state.currentInvoice, ...updatedInvoice };
        }
      })
      .addCase(updateInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update Invoice Status
      .addCase(updateInvoiceProcessingStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateInvoiceProcessingStatus.fulfilled, (state, action) => {
        state.loading = false;
        const updatedInvoice = action.payload;
        
        // Update the invoice in the invoices list
        const index = state.invoices.findIndex(invoice => invoice.id === updatedInvoice.id);
        if (index !== -1) {
          // Ensure we preserve all existing fields and update with new data
          state.invoices[index] = { ...state.invoices[index], ...updatedInvoice };
        }
        
        // Update currentInvoice if it's the same invoice
        if (state.currentInvoice?.id === updatedInvoice.id) {
          state.currentInvoice = { ...state.currentInvoice, ...updatedInvoice };
        }
      })
      .addCase(updateInvoiceProcessingStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete Invoice
      .addCase(deleteInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = state.invoices.filter(invoice => invoice.id !== action.payload);
        if (state.currentInvoice?.id === action.payload) {
          state.currentInvoice = null;
        }
      })
      .addCase(deleteInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Bulk Delete Invoices
      .addCase(bulkDeleteInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkDeleteInvoices.fulfilled, (state, action) => {
        state.loading = false;
        // Remove deleted invoices from the list
        state.invoices = state.invoices.filter(invoice => !action.payload.ids.includes(invoice.id));
        if (state.currentInvoice && action.payload.ids.includes(state.currentInvoice.id)) {
          state.currentInvoice = null;
        }
      })
      .addCase(bulkDeleteInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Bulk Update Invoice Status
      .addCase(bulkUpdateInvoiceStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
             .addCase(bulkUpdateInvoiceStatus.fulfilled, (state, action) => {
         state.loading = false;
         // Update status field for invoices in the list (same as "Stav" field)
         state.invoices = state.invoices.map(invoice => {
           if (action.payload.ids.includes(invoice.id)) {
             return { ...invoice, status: action.payload.status as any };
           }
           return invoice;
         });
         if (state.currentInvoice && action.payload.ids.includes(state.currentInvoice.id)) {
           state.currentInvoice = { ...state.currentInvoice, status: action.payload.status as any };
         }
       })
      .addCase(bulkUpdateInvoiceStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Get Ready Invoices
      .addCase(getReadyInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getReadyInvoices.fulfilled, (state, action) => {
        state.loading = false;
        // This action is used for fetching ready invoices, but we don't need to update the state
        // as it's just for the ABO creation dialog
      })
      .addCase(getReadyInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Process Invoice with AI
      .addCase(processInvoiceWithAI.pending, (state) => {
        state.aiProcessing.isProcessing = true;
        state.aiProcessing.currentStep = 'ai_extraction';
        state.aiProcessing.progress = 0;
        state.aiProcessing.error = null;
        state.aiProcessing.statusHistory = [];
      })
      .addCase(processInvoiceWithAI.fulfilled, (state, action) => {
        state.aiProcessing.isProcessing = false;
        state.aiProcessing.currentStep = 'complete';
        state.aiProcessing.progress = 100;
        state.aiProcessing.extractedData = action.payload.extractedData || null;
        state.aiProcessing.statusHistory = action.payload.statusHistory || [];
      })
      .addCase(processInvoiceWithAI.rejected, (state, action) => {
        state.aiProcessing.isProcessing = false;
        state.aiProcessing.currentStep = 'error';
        state.aiProcessing.error = action.payload as string;
      })
      // Create Invoice from AI
      .addCase(createInvoiceFromAI.pending, (state) => {
        state.aiProcessing.isCreating = true;
        state.aiProcessing.error = null;
      })
      .addCase(createInvoiceFromAI.fulfilled, (state, action) => {
        state.aiProcessing.isCreating = false;
        // Add the new invoice to the list
        if (action.payload.invoice) {
          state.invoices.unshift(action.payload.invoice);
        }
        // Reset AI processing state after successful creation
        state.aiProcessing = {
          isProcessing: false,
          isCreating: false,
          currentStep: 'idle',
          progress: 0,
          extractedData: null,
          statusHistory: [],
          error: null,
        };
      })
      .addCase(createInvoiceFromAI.rejected, (state, action) => {
        state.aiProcessing.isCreating = false;
        state.aiProcessing.error = action.payload as string;
      });
  },
});

export const { clearError, clearCurrentInvoice, setPagination, clearAIProcessing, clearAIError } = invoiceSlice.actions;
export default invoiceSlice.reducer; 